import { MarketplaceHeader } from '../components/MarketplaceHeader'
import { MarketplaceFooter } from '../components/MarketplaceFooter'
import { MarketplaceLocationPrompt } from '../components/MarketplaceLocationPrompt'
import { PreferencesBanner } from '../components/PreferencesBanner'
import { UserPreferencesModal } from '../components/UserPreferencesModal'
import { ArenaCarousel } from '../components/sdui/ArenaCarousel'
import { TrainingCenterSection } from '../components/sdui/TrainingCenterSection'
import { UpcomingReservationsCarousel } from '../components/sdui/UpcomingReservationsCarousel'
import { HomeUpcomingDayUseOccurrences } from '../components/sdui/HomeUpcomingDayUseOccurrences'
import { SuggestArenaEmptyState } from '../components/SuggestArenaEmptyState'
import { ExploreBanner } from '../components/ExploreBanner'
import type { HomeResponse } from '../types/sdui'
import type { SportModality } from '../types/address'
import { useState, useEffect } from 'react'
import { useUserLocation } from '../hooks/useUserLocation'
import { useUserPreferences } from '../hooks/useUserPreferences'
import { useAuth } from '../contexts/AuthContext'
import { homeService } from '../services/homeService'
import { analyticsService } from '@meiuca/analytics'
import { Button, ButtonVariant } from '@meiuca/ui-components'

export function MarketplacePage() {
  const [locationPromptOpen, setLocationPromptOpen] = useState(false)
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false)
  const { userLocation, isLoading: isLoadingLocation } = useUserLocation()
  const { isAuthenticated } = useAuth()
  const { savePreferences, shouldShowModal } = useUserPreferences({
    autoLoad: true,
    isAuthenticated
  })
  const [home, setHome] = useState<HomeResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Determine if banner should be shown (for all users, logged in or not)
  const showBanner = shouldShowModal

  // Track screen view on mount
  useEffect(() => {
    analyticsService.trackScreenView('marketplace_home')
  }, [])

  // Mirror mobile: a location is only usable when it carries real coordinates.
  const coordinate = userLocation?.coordinate
  const hasCoordinates = !!coordinate && (coordinate.latitude !== 0 || coordinate.longitude !== 0)
  const coordinateKey = hasCoordinates
    ? `${coordinate!.latitude},${coordinate!.longitude}`
    : null

  // Fetch structured home payload from backend
  useEffect(() => {
    // Wait for location hook to finish loading before deciding
    if (isLoadingLocation) {
      return
    }

    // Mirror mobile: without a location we show the needs-location state and
    // never hit the backend (the home is location-scoped).
    if (!hasCoordinates) {
      setHome(null)
      setError(null)
      setIsLoading(false)
      return
    }

    const fetchHome = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const data = await homeService.getHome({
          lat: coordinate!.latitude,
          lng: coordinate!.longitude
        })
        setHome(data)
      } catch (err) {
        console.error('Error fetching home:', err)
        setError('Erro ao carregar a página. Tente novamente.')
        setHome(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchHome()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingLocation, coordinateKey])

  // Handle preferences save
  const handlePreferencesSave = async (modalities: SportModality[]) => {
    await savePreferences(modalities)
    setPreferencesModalOpen(false)
  }

  return (
    <div className="min-h-screen bg-background-secondary">
      {/* Header */}
      <MarketplaceHeader
        onLocationClick={() => setLocationPromptOpen(true)}
      />

      {/* Preferences Banner - Fixed at top */}
      {showBanner && (
        <PreferencesBanner onOpenModal={() => setPreferencesModalOpen(true)} />
      )}

      {/* User Preferences Modal */}
      <UserPreferencesModal
        isOpen={preferencesModalOpen}
        onClose={() => setPreferencesModalOpen(false)}
        onSave={handlePreferencesSave}
        title="Quais esportes você pratica?"
        description="Conte pra gente para vermos as melhores arenas para você"
        allowSkip={true}
      />

      {/* Location Prompt Modal */}
      <MarketplaceLocationPrompt
        open={locationPromptOpen}
        onClose={() => setLocationPromptOpen(false)}
      />

      {/* Loading State */}
      {(isLoadingLocation || (hasCoordinates && isLoading)) && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      {/* No-location prompt — mirrors mobile's needs-location empty state */}
      {!isLoadingLocation && !hasCoordinates && (
        <div className="flex items-center justify-center min-h-[400px] px-4">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold text-text-primary mb-2">
              Onde você está?
            </h2>
            <p className="text-text-secondary mb-6">
              Selecione sua localização para ver arenas, horários e quadras perto de você.
            </p>
            <Button variant={ButtonVariant.PRIMARY} onClick={() => setLocationPromptOpen(true)}>
              Selecionar localização
            </Button>
          </div>
        </div>
      )}

      {/* Structured content — composed by the client */}
      {hasCoordinates && !isLoading && !error && home && (
        <div className="pb-8">
          {home.upcomingReservations.length > 0 && (
            <UpcomingReservationsCarousel
              config={{
                id: 'upcoming-reservations',
                title: 'Próximas reservas',
                reservations: home.upcomingReservations,
                ctaText: 'Ver todas',
                ctaLink: '/reservations'
              }}
            />
          )}

          <ExploreBanner />

          {home.nearbyArenas.length > 0 ? (
            <ArenaCarousel
              config={{
                id: 'nearby-arenas',
                title: 'Perto de você',
                arenas: home.nearbyArenas,
                ctaText: 'Ver todas',
                ctaLink: '/arenas/search'
              }}
            />
          ) : (
            <SuggestArenaEmptyState />
          )}

          <TrainingCenterSection centers={home.trainingCenters ?? []} />

          {home.dayUseOccurrences.length > 0 && (
            <HomeUpcomingDayUseOccurrences
              config={{
                id: 'home-upcoming-occurrences',
                title: 'Próximos Day Uses (Rotativos)',
                occurrences: home.dayUseOccurrences
              }}
            />
          )}
        </div>
      )}

      <MarketplaceFooter />
    </div>
  )
}
